/**
 * Scanner de QR embarcado (padrão Tupi/Spott/Move — o usuário está acostumado
 * a escanear DENTRO do app). Lê os MESMOS QRs físicos que hoje abrem a landing
 * `bow.app.br/qr/...` no navegador; dentro do app o destino é direto:
 *
 *   /qr/{hub}                    → /scan/{hub} (resolve: 1 conector = auto-start
 *                                  via QRHandler; N conectores = ConfigureCharge)
 *   /qr/{hub}/{connectorNumber}  → QRHandler (auto-start, rota existente)
 *   /amenities/{uuid}/vacuum     → VacuumOffer (QR do aspirador = URL da rota)
 *   /convite/{CODE}              → Convite (QR/link do Indique e Ganhe)
 *
 * A câmera em si é `useBarcodeScanner` + `ScanQrOverlay` (@capacitor-mlkit/
 * barcode-scanning — troca do @capacitor/barcode-scanner original porque a UI
 * nativa dele era fixa: sem lanterna, sem viewfinder maior, sem overlay
 * pra "digitar código" — feedback Saulo 2026-07-06, ref. Tupi/Spott).
 */

const BOW_HOSTS = new Set([
  'bow.app.br',
  'www.bow.app.br',
  'dev.bow.app.br',
  'localhost'
]);

/**
 * Converte o texto escaneado num path interno do app, ou null se não for um
 * QR Bow reconhecido. Restrito aos 3 formatos conhecidos — QR de terceiros
 * (Tupi/Spott/Move imprimem os próprios) não navega.
 */
export function parseBowQr(text: string): string | null {
  let url: URL;
  try {
    url = new URL(text.trim());
  } catch {
    return null;
  }
  if (!BOW_HOSTS.has(url.hostname)) return null;

  const path = url.pathname.replace(/\/+$/, '');

  // /qr/{hub}/{connectorNumber} — deeplink de conector (auto-start)
  if (/^\/qr\/[^/]+\/\d+$/.test(path)) return path;

  // /qr/{hub} — QR físico do carregador (station-level)
  const station = /^\/qr\/([^/]+)$/.exec(path);
  if (station) return `/scan/${encodeURIComponent(station[1])}`;

  // /amenities/{stationId}/vacuum — QR do aspirador (formato antigo).
  // `origin=direct`: veio do adesivo no equipamento, não de uma tela do app.
  const vacuumLegacy = /^\/amenities\/([^/]+)\/vacuum$/.exec(path);
  if (vacuumLegacy)
    return `/amenities/${encodeURIComponent(vacuumLegacy[1])}/vacuum?origin=direct`;

  // /v/{stationId} — QR físico do aspirador (formato curto, 2026-07-08).
  // Mesmo QR pra todos: na câmera abre a página pública (Pix avulso);
  // dentro do app normaliza pro fluxo logado com saldo.
  const vacuum = /^\/v\/([^/]+)$/.exec(path);
  if (vacuum) return `/amenities/${encodeURIComponent(vacuum[1])}/vacuum?origin=direct`;

  // /convite/{CODE} — link/QR de indicação (Indique e Ganhe). Dentro do app
  // abre a landing do convite, que guarda o código e (logado) aplica na conta.
  const convite = /^\/convite\/([A-Za-z0-9-]{3,24})$/.exec(path);
  if (convite) return `/convite/${encodeURIComponent(convite[1])}`;

  return null;
}
